import {
  Avatar,
  Box,
  makeStyles,
  Tooltip,
  Typography,
} from "@material-ui/core";
import React from "react";
import { useSelector } from "react-redux";
import {
  getTeamColorById,
  getTeamNameByTeamId,
} from "../../../store/hub/teams";
import { getUserImageById, getUserNameById } from "../../../store/hub/users";
import { getLoggedInUserId } from "../../../store/hub/loggedInUser";
import BuzzHubsIcon from "../../../assets/images/logo_small.png";

export default function ThreadAvatar({ thread }) {
  const classes = useStyles();
  const isUserFl = thread.UserIds.length >= 1;
  const isTeamFl = thread.TeamIds.length >= 1;
  const isMultiImage = thread.UserIds.length > 1 || thread.TeamIds.length > 1;
  const getUserImageByIdSelector = useSelector(getUserImageById);
  const getTeamColorByIdSelector = useSelector(getTeamColorById);
  const getTeamNameByIdSelector = useSelector(getTeamNameByTeamId);
  const getUserNameByIdSelector = useSelector(getUserNameById);
  const loggedInUserId = useSelector(getLoggedInUserId)();

  // Check if this is a system/BuzzHubs message (fromUserId is null) and the logged in user is the recipient
  // This happens when UserIds and TeamIds are empty (system message to logged in user)
  // OR when UserIds contains only the logged in user
  const isSystemMessageToLoggedInUser = 
    thread.LatestMessage?.UserId === null && 
    ((thread.UserIds.length === 0 && thread.TeamIds.length === 0) ||
     (thread.UserIds.length === 1 && thread.UserIds.includes(loggedInUserId)));

  const ids = isUserFl ? thread.UserIds : thread.TeamIds;

  return (
    <>
      {isTeamFl ? (
        <Tooltip
          title={
            <Box>
              {ids.map((id, index) => {
                if (index === 10)
                  return (
                    <Typography
                      key={`threadAvatarTooltip${id}`}
                      variant="body2"
                    >
                      + {ids.length - 9} more hubs
                    </Typography>
                  );
                if (index > 10) return null;
                return (
                  <Typography key={`threadAvatarTooltip${id}`} variant="body2">
                    {getTeamNameByIdSelector(id)}
                  </Typography>
                );
              })}
            </Box>
          }
        >
          {isMultiImage
            ? multiTeamJsx(
                classes,
                getTeamColorByIdSelector,
                ids,
                getTeamNameByIdSelector
              )
            : singleTeamJsx(
                classes,
                getTeamColorByIdSelector,
                ids,
                getTeamNameByIdSelector
              )}
        </Tooltip>
      ) : isUserFl ? (
        <Tooltip
          title={
            <Box>
              {ids.map((id, index) => {
                if (index === 10)
                  return (
                    <Typography
                      key={`threadAvatarTooltip${id}`}
                      variant="body2"
                    >
                      + {ids.length - 9} more users
                    </Typography>
                  );
                if (index > 10) return null;
                return (
                  <Typography key={`threadAvatarTooltip${id}`} variant="body2">
                    {getUserNameByIdSelector(id)}
                  </Typography>
                );
              })}
            </Box>
          }
        >
          {isMultiImage
            ? multiUserJsx(
                classes,
                getUserImageByIdSelector,
                ids,
                getUserNameByIdSelector,
                isSystemMessageToLoggedInUser
              )
            : singleUserJsx(
                classes,
                getUserImageByIdSelector,
                ids,
                getUserNameByIdSelector,
                isSystemMessageToLoggedInUser
              )}
        </Tooltip>
      ) : null}

      {!isTeamFl && !isUserFl ? singleUnknownUserJsx(classes, isSystemMessageToLoggedInUser) : null}
    </>
  );
}

const singleUnknownUserJsx = (classes, isSystemMessageToLoggedInUser) => {
  if (!classes) return;

  // If this is a system message to the logged in user, show BuzzHubs logo
  const avatarSrc = isSystemMessageToLoggedInUser ? BuzzHubsIcon : undefined;

  return (
    <Avatar 
      alt="thread-user" 
      src={avatarSrc}
      className={classes.messageThreadImage}
    />
  );
};

const multiTeamJsx = (classes, getTeamColor, ids, getTeamName) => {
  if (!ids || !getTeamColor || !classes || !getTeamName) return;

  return (
    <Box className={classes.multiUserBox}>
      <Avatar
        alt="thread-user"
        className={classes.smallMessageThreadImage}
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          backgroundColor: `#${getTeamColor(ids[0])}`,
        }}
      >
        {getTeamName(ids[0]).charAt(0)}
      </Avatar>
      <Avatar
        alt="thread-user"
        className={classes.smallMessageThreadImage}
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          backgroundColor: `#${getTeamColor(ids[1])}`,
        }}
      >
        {getTeamName(ids[1]).charAt(0)}
      </Avatar>
    </Box>
  );
};

const singleTeamJsx = (classes, getTeamColor, ids, getTeamName) => {
  if (!ids || !getTeamColor || !classes || !getTeamName) return;

  return (
    <Avatar
      alt="thread-user"
      className={classes.messageThreadImage}
      style={{ backgroundColor: `#${getTeamColor(ids[0])}` }}
    >
      {getTeamName(ids[0]).charAt(0)}
    </Avatar>
  );
};

const multiUserJsx = (classes, getUserImage, ids, getUserName, isSystemMessageToLoggedInUser) => {
  if (!ids || !getUserImage || !classes || !getUserName) return;

  // Get user images, using BuzzHubs logo for system messages or when image is null
  let image0 = getUserImage(ids[0]);
  if (isSystemMessageToLoggedInUser && ids[0] && !image0) {
    image0 = BuzzHubsIcon;
  } else if (!image0 || image0 === "BuzzHubs") {
    image0 = BuzzHubsIcon;
  }

  let image1 = getUserImage(ids[1]);
  if (!image1 || image1 === "BuzzHubs") {
    image1 = BuzzHubsIcon;
  }

  return (
    <Box className={classes.multiUserBox}>
      <Avatar
        alt="thread-user"
        src={image0}
        className={classes.smallMessageThreadImage}
        style={{ position: "absolute", top: 0, right: 0 }}
      />
      <Avatar
        alt="thread-user"
        src={image1}
        className={classes.smallMessageThreadImage}
        style={{ position: "absolute", bottom: 0, left: 0 }}
      />
    </Box>
  );
};

const singleUserJsx = (classes, getUserImage, ids, getUserName, isSystemMessageToLoggedInUser) => {
  if (!ids || !getUserImage || !classes) return;

  // Get the user image
  let userImage = getUserImage(ids[0]);
  
  // If this is a system message to the logged in user, always use BuzzHubs logo
  // Otherwise, if the image is null/undefined/"BuzzHubs" string, use BuzzHubs logo as fallback
  if (isSystemMessageToLoggedInUser) {
    userImage = BuzzHubsIcon;
  } else if (!userImage || userImage === "BuzzHubs") {
    userImage = BuzzHubsIcon;
  }

  return (
    <Avatar
      alt="thread-user"
      src={userImage}
      className={classes.messageThreadImage}
    />
  );
};

const useStyles = makeStyles((theme) => ({
  multiUserBox: {
    position: "relative",
    width: "50px",
    height: "50px",
    marginTop: "auto",
    marginBottom: "auto",
  },
  messageThreadImage: {
    marginTop: "auto",
    marginBottom: "auto",
    width: 50,
    height: 50,
  },
  smallMessageThreadImage: {
    width: 35,
    height: 35,
  },
}));
